import { useState } from 'react';
import Badge from '../shared/Badge';
import Dropdown from '../shared/Dropdown';
import { IconButton } from '../shared/StandardButtons';
import { adminApi } from '../../lib/admin-api';
import toast from 'react-hot-toast';

export default function AIModelsModal({ isOpen, onClose, models, providers, isLoading, onRefresh }) {
  const [expandedModelId, setExpandedModelId] = useState(null);
  const [editingModelId, setEditingModelId] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedModelIdentifier, setEditedModelIdentifier] = useState('');
  const [editedType, setEditedType] = useState('');
  const [editedProvider, setEditedProvider] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  if (!isOpen) return null;

  const toggleExpand = (modelId) => {
    if (expandedModelId === modelId) {
      // Collapse
      setExpandedModelId(null);
      setEditingModelId(null);
    } else {
      // Expand
      setExpandedModelId(modelId);
      setEditingModelId(null);
    }
  };

  const handleEdit = (model) => {
    setEditingModelId(model.id);
    setEditedName(model.name || '');
    setEditedDescription(model.description || '');
    setEditedModelIdentifier(model.model_identifier || '');
    setEditedType(model.type || '');
    setEditedProvider(model.provider || '');
  };

  const handleCancel = () => {
    setEditingModelId(null);
    setEditedName('');
    setEditedDescription('');
    setEditedModelIdentifier('');
    setEditedType('');
    setEditedProvider('');
  };

  const handleSave = async (model) => {
    try {
      setIsSaving(true);
      const updates = {
        name: editedName,
        description: editedDescription,
        model_identifier: editedModelIdentifier,
        type: editedType,
        provider: editedProvider
      };

      await adminApi.aiModels.update(model.id, updates);
      toast.success('Model updated successfully');
      setEditingModelId(null);
      setEditedName('');
      setEditedDescription('');
      setEditedModelIdentifier('');
      setEditedType('');
      setEditedProvider('');
      await onRefresh();
    } catch (error) {
      console.error('Failed to update model:', error);
      toast.error('Failed to update model');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (model) => {
    if (!confirm(`Are you sure you want to delete "${model.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await adminApi.aiModels.delete(model.id);
      toast.success('Model deleted successfully');
      if (expandedModelId === model.id) {
        setExpandedModelId(null);
        setEditingModelId(null);
      }
      await onRefresh();
    } catch (error) {
      console.error('Failed to delete model:', error);
      toast.error('Failed to delete model');
    }
  };

  // Filter models
  const filteredModels = models.filter(model => {
    const matchesSearch = !searchQuery ||
      model.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      model.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      model.model_identifier?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = !typeFilter || model.type === typeFilter;
    return matchesSearch && matchesType;
  });

  // Get unique types for filter
  const uniqueTypes = [...new Set(models.map(m => m.type).filter(Boolean))];
  const typeOptions = [
    { value: '', label: 'All Types' },
    ...uniqueTypes.map(type => ({ value: type, label: type }))
  ];

  const modelTypeOptions = [
    { value: 'generative', label: 'Generative' },
    { value: 'embedding', label: 'Embedding' },
    { value: 'importing', label: 'Importing' }
  ];

  // Create provider options dynamically from backend data
  const providerOptions = (providers || []).map(provider => ({
    value: provider.value || provider,
    label: provider.label || (provider.charAt(0).toUpperCase() + provider.slice(1))
  }));

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }} onClick={onClose}>
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 border border-gray-700 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-white">AI Models</h3>
              <p className="text-gray-400 text-sm mt-1">Manage AI models available to the system</p>
            </div>
            <IconButton onClick={onClose} title="Close">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </IconButton>
          </div>

          {/* Search and Filter */}
          <div className="flex gap-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search models..."
                className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
              />
              <svg className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <Dropdown
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              options={typeOptions}
              className="w-48 bg-gray-900 border-gray-700 focus:border-purple-500"
            />
          </div>
        </div>

        {/* Model List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          ) : filteredModels.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <p className="text-lg font-medium mb-2">No Models Found</p>
              <p className="text-sm">
                {searchQuery || typeFilter ? 'Try adjusting your filters' : 'No AI models are currently configured'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {filteredModels.map((model) => {
                const isExpanded = expandedModelId === model.id;
                const isEditing = editingModelId === model.id;

                return (
                  <div key={model.id} className="border-b border-gray-700 last:border-b-0">
                    {/* Collapsed Header */}
                    <div
                      onClick={() => toggleExpand(model.id)}
                      className="flex items-center gap-3 p-4 transition-colors cursor-pointer"
                      style={{
                        backgroundColor: 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      {/* Chevron */}
                      <div className="text-gray-400">
                        <svg className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0 flex items-center justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="text-white font-medium truncate">{model.name}</h4>
                          {model.description && (
                            <p className="text-sm text-gray-400 truncate">{model.description}</p>
                          )}
                        </div>
                        <Badge
                          variant={model.type === 'generative' ? 'blue' : model.type === 'embedding' ? 'green' : 'purple'}
                          size="sm"
                        >
                          {model.type}
                        </Badge>
                      </div>

                      {/* Delete Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(model);
                        }}
                        className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                        title="Delete model"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="px-4 py-4 bg-gray-900/50">
                        <div className="border border-gray-700 rounded-lg p-4 space-y-4">
                          {/* Row 1: Model Name and Model Identifier */}
                          <div className="grid grid-cols-2 gap-4">
                            {/* Name Field */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-200 mb-2">
                                Model Name
                              </label>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editedName}
                                  onChange={(e) => setEditedName(e.target.value)}
                                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                                  placeholder="Enter model name..."
                                />
                              ) : (
                                <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
                                  <p className="text-sm text-gray-300">{model.name || '(No name set)'}</p>
                                </div>
                              )}
                            </div>

                            {/* Model Identifier Field */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-200 mb-2">
                                Model Identifier
                              </label>
                              {isEditing ? (
                                <input
                                  type="text"
                                  value={editedModelIdentifier}
                                  onChange={(e) => setEditedModelIdentifier(e.target.value)}
                                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm font-mono focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                                  placeholder="e.g., gemini-2.5-flash"
                                />
                              ) : (
                                <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
                                  <p className="text-sm text-gray-300 font-mono">{model.model_identifier || '(No identifier set)'}</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Description Field */}
                          <div>
                            <label className="block text-sm font-semibold text-gray-200 mb-2">
                              Description
                            </label>
                            {isEditing ? (
                              <textarea
                                value={editedDescription}
                                onChange={(e) => setEditedDescription(e.target.value)}
                                rows={3}
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20"
                                placeholder="Enter model description..."
                              />
                            ) : (
                              <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
                                <p className="text-sm text-gray-300">{model.description || '(No description set)'}</p>
                              </div>
                            )}
                          </div>

                          {/* Row 2: Type and Provider */}
                          <div className="grid grid-cols-2 gap-4">
                            {/* Type Field */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-200 mb-2">
                                Type
                              </label>
                              {isEditing ? (
                                <Dropdown
                                  value={editedType}
                                  onChange={(e) => setEditedType(e.target.value)}
                                  options={modelTypeOptions}
                                  className="w-full"
                                />
                              ) : (
                                <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
                                  <p className="text-sm text-gray-300">{model.type || '(No type set)'}</p>
                                </div>
                              )}
                            </div>

                            {/* Provider Field */}
                            <div>
                              <label className="block text-sm font-semibold text-gray-200 mb-2">
                                Provider
                              </label>
                              {isEditing ? (
                                <Dropdown
                                  value={editedProvider}
                                  onChange={(e) => setEditedProvider(e.target.value)}
                                  options={providerOptions}
                                  className="w-full"
                                />
                              ) : (
                                <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
                                  <p className="text-sm text-gray-300 capitalize">{model.provider || '(No provider set)'}</p>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Edit Button at Bottom */}
                          <div className="flex justify-end gap-2 pt-2">
                            {isEditing ? (
                              <>
                                <button
                                  onClick={handleCancel}
                                  disabled={isSaving}
                                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 text-white rounded-lg text-sm transition-all"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => handleSave(model)}
                                  disabled={isSaving}
                                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-700 rounded-lg text-sm transition-all flex items-center gap-2"
                                  style={{ color: 'var(--color-text-on-accent)' }}
                                >
                                  {isSaving ? (
                                    <>
                                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                      </svg>
                                      Save
                                    </>
                                  )}
                                </button>
                              </>
                            ) : (
                              <button
                                onClick={() => handleEdit(model)}
                                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg text-sm transition-all flex items-center gap-2"
                              >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                                Edit
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-700 flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-all duration-200"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
